declare var qz: any;
declare var RSVP:any;

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { ToasterService } from 'angular2-toaster';

import { environment } from '../../environments/environment';
import {
  ACTIVE,
  INACTIVE,
  WAITING
} from '../../ngrx/printer-statuses';

import {
  SET_BATCH_LIST_VIEW,
  SET_SHIPMENT_DETAILS_VIEW,
  RESET_PRINTER_CONFIG_VIEW,
  SET_PRINTER_CONFIG_VIEW,
  SET_JOB_DETAILS_VIEW,
  SET_MAIN_VIEW
} from '../../ngrx/actions';
declare var TextDecoder;
import {
  CUT,
  PREPRINT,
  QUEUED
} from '../../ngrx/status-types';

import { Job, Order, Shipment, Batch } from '../models';

import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';
import { ShipmentService } from './shipment.service';
import { OrderService } from './order.service';
import { TranslateService } from '../i18n';
import { BsModalRef } from 'ngx-bootstrap/modal';

const errorCodes = [400, 401, 403, 404];

const specialInstructionPage = {
  '727826479700030': 'https://s3.amazonaws.com/weprint.sheet.public/WP_I_WaterBottleLabels.pdf',
  '727826479700099': 'https://s3.amazonaws.com/weprint.sheet.public/WP_I_WaterBottleLabels.pdf',
  '727826480100030': 'https://s3.amazonaws.com/weprint.sheet.public/WP_I_LipBalmLabels.pdf',
  '727826480100099': 'https://s3.amazonaws.com/weprint.sheet.public/WP_I_LipBalmLabels.pdf'
};


@Injectable()
export class PrinterService {
  private ghsBucket: string = environment.imposerGHSBucket;
  emitPrintManifest   : Subject<any> = new Subject();
  constructor(
    private authService      : AuthenticationService,
    private credentialService: CredentialService,
    private orderService     : OrderService,
    private shipmentService  : ShipmentService,
    private store            : Store<any>,
    private toasterService   : ToasterService,
    private translate        : TranslateService,
  ) {}

  private ABToString(ab: any): string {
    return new Uint8Array(ab)
      .reduce((p, c) => p + String.fromCharCode(c), '');
  }

  private sign(toSign: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.printerSignFunction,
        Payload     : JSON.stringify(toSign)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              :         data.Payload;
            observer.next(output);
          }
        }
      );
    });
  }

  private generatePackingSlip(html: any, qty: number, isRfid: boolean): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: isRfid ? environment.printRFIDPackingSlipFunction : environment.printerPackingSlipFunction,
        Payload     : JSON.stringify(html)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              :         data.Payload;
            if(output.pdfS3) {
              const params = {
                Bucket: 'impreso.packingslips',
                Key: output.pdfS3
              };
    
              this.credentialService.s3.getObject(params, function(err, data) {
                if (err) {
                  console.error(err); // an error occurred
                } else {
                  const string = new TextDecoder('utf-8').decode(data.Body);
                  observer.next({pdf_base64:string});
                }
              });
            } else {
              for (let i = 1; i <= qty; i++) {
                observer.next(output);
              }
            }
          }
        }
      );
    });
  }

  private generatePDFAddons(urls: string[]): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.printerPDFAddonsFunction,
        Payload     : JSON.stringify({ urls })
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              :     data.Payload;

            observer.next(output);
          }
        }
      );
    });
  }

  private generatePackagingLabel(labelContent: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.printerPackagingLabelFunction,
        Payload     : JSON.stringify(labelContent)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              :         data.Payload;
            observer.next(output);
          }
        }
      );
    });
  }

  private getSignedCertificate(): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.printerCertFunction
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              :         data.Payload;
            observer.next(output);
          }
        }
      );
    });
  }

  private getWorkOrder(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.printWorkOrderFunction,
        Payload     : JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              :         data.Payload;
            observer.next(output);
          }
        }
      ); 
    });
  }

  private getRfidWorkOrder(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.printRfidWorkOrderFunction,
        Payload     : JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              :         data.Payload;
            observer.next(output);
          }
        }
      ); 
    });
  }

  private getPtoArticleChecklist(payload: any): Observable<any> {
    return new Observable(observer => {
      this.credentialService.lambda.invoke({
        FunctionName: environment.printPtoArticleChecklistFunction,
        Payload     : JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              :         data.Payload;
            observer.next(output);
          }
        }
      ); 
    });
  }

  private async getWorkOrderData(payload: any) {
      return new Promise(async (resolve, reject)=>{ 
         await this.credentialService.lambda.invoke({
        FunctionName: environment.printWorkOrderFunction,
        Payload     : JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              :         data.Payload;
              console.log(output)
            resolve(output);
          }
        }
      ); 
    })
  }

  private getLaserWorkOrder(payload: any): Observable<any> {
    return new Observable(observer => {
      this.credentialService.lambda.invoke({
        FunctionName: environment.laserWorkOrderFunction,
        Payload     : JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              :         data.Payload;
            observer.next(output);
          }
        }
      ); 
    });
  }

  getPrinterConfigList(): Observable<any> {
    return this.store.select('printers', 'printerConfiguration');
  }

  endConnection(): void {
    this.store.dispatch({
      type   : SET_PRINTER_CONFIG_VIEW,
      payload: { status: WAITING }
    });

    if (!qz.websocket.isActive()) {
      console.log(`No active connection with QZ tray exists`);
      this.store.dispatch({ type: RESET_PRINTER_CONFIG_VIEW });
    } else {
      qz.websocket
        .disconnect()
        .then(res => {
          console.log(`Disconnected from QZ tray`);
          this.store.dispatch({ type: RESET_PRINTER_CONFIG_VIEW });
        })
        .catch(err => {
          console.log(`End connection error`);
        });
    }
  }

  findDefaultPrinter(): void {
    qz.printers
      .getDefault()
      .then(printer => {
        console.log(printer);

        this.store.dispatch({
          type   : SET_PRINTER_CONFIG_VIEW,
          payload: {
            selectedPrinter: printer,
            printer
          }
        });
      })
      .catch(err => console.log(`Find default printer error: ${JSON.stringify(err)}`));
  }

  findPrinter(query: string): void {
    const search = (query || '').trim();

    if (!search) this.findDefaultPrinter();
    else {
      qz.printers
        .find(search)
        .then(printer => {
          console.log(printer);

          this.store.dispatch({
            type   : SET_PRINTER_CONFIG_VIEW,
            payload: {
              selectedPrinter: printer,
              printer
            }
          });
        })
        .catch(err => {
          this.store.dispatch({
            type   : SET_PRINTER_CONFIG_VIEW,
            payload: { error: `"${query}" does not match any existing printers on the network.` }
          });
        })
    }
  }

  async findPrinters(): Promise<any> {
    try {
      const printers = await qz.printers.find();
      console.log(printers);
  
      this.store.dispatch({
        type   : SET_PRINTER_CONFIG_VIEW,
        payload: { printers }
      });

      return;
    } catch (err) {
      console.log(`Find printers error`);
      console.log(err);
      return err;
    }
  }

  async findVersion(): Promise<any> {
    try {
      const version = await qz.api.getVersion();

      this.store.dispatch({
        type   : SET_PRINTER_CONFIG_VIEW,
        payload: { version }
      });

      return;
    } catch (err) {
      console.log(`Find version error`);
      console.log(err);
      return err;
    }
  }

  getDestinationPrinter(printerDestination: string): any {
    const printConfig = this.getPrintConfig();

    switch (printerDestination) {
      case 'shipping': 
        return printConfig.shipping;
      case 'fedex_shipping': 
        return printConfig.fedex_shipping;
      case 'packingSlip': 
        return printConfig.packingSlip;
      case 'packagingLabel': 
        return printConfig.packagingLabel;
      case 'workOrder': 
        return printConfig.workOrder;
      case 'rfidWorkOrder':
        return printConfig.rfidWorkOrder;
      case 'ptoArticleChecklist':
        return printConfig.ptoArticleChecklist;
      case 'addressLabel': 
        return printConfig.addressLabel;
      case 'ghs':
        return printConfig.ghs;
      case 'ptoWorkOrder':
        return printConfig.ptoWorkOrder;
      case 'ptoPackagingLabel':
        return printConfig.ptoPackagingLabel;
      case 'rfidInnerCaseLabel':
        return printConfig.rfidInnerCaseLabel;
      case 'rfidBoxLabels': // Added new box
        return printConfig.rfidBoxLabels; // Replace with actual config
      default: 
        return printConfig.defaultPrinter;
    }
  }

  getPrintConfig(): any {
    const printConfig = JSON.parse(localStorage.getItem('printConfig'));

    if (!printConfig) {
      const newConfig = {
        defaultPrinter    : 'NONE',
        fedex_shipping    : 'NONE',
        packingSlip       : 'NONE',
        shipping          : 'NONE',
        workOrder         : 'NONE',
        packagingLabel    : 'NONE',
        addressLabel      : 'NONE',
        ghs               : 'NONE',
        ptoWorkOrder      : 'NONE',
        ptoPackagingLabel : 'NONE',
        rfidInnerCaseLabel: 'NONE',
        rfidBoxLabels        : 'NONE',
        rfidWorkOrder        : 'NONE',
        ptoArticleChecklist  : 'NONE',
      };

      localStorage.setItem('printConfig', JSON.stringify(newConfig));

      return newConfig;
    } else {
      return printConfig;
    }
  }

  getGHSImposedPDF(key: string): Observable<any> {
    return new Observable(observer => {
      this.credentialService.s3.getSignedUrl('getObject', {
        Bucket: this.ghsBucket,
        Key: key,
      }, (err: any, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
          }
          observer.error(err);
        } else {
          observer.next(data);
        }
      });
    });
  }

  async getPrinterList(): Promise<any> {
    try {
      const printers = await qz.printers.find();

      if (!printers || !printers.length) {
        console.log(`No active printers available`);
        return [];
      }

      return printers;
    } catch (err) {
      console.log(`getPrinterList error`);
      console.log(err);
      return [];
    }
  }

  launchQZ(): void {
    if (!qz.websocket.isActive()) {
      window.location.assign('qz:launch');
      // Retry 5 times, pausing 5 seconds between each attempt
      this.startConnection({ retries: 5, delay: 5 });
    }
  }

  async listNetworkInfo(): Promise<any> {
    try {
      const networkInfo = await qz.websocket.getNetworkInfo();
      
      const { macAddress, ipAddress } = networkInfo;

      if (macAddress && ipAddress) {
        let macFormatted = '';
        for (let i = 0; i < macAddress.length; i++) {
          macFormatted += macAddress[i];

          if (i % 2 === 1 && i < macAddress.length - 1) {
            macFormatted += ':';
          }
        }

        const networkInfo = { ipAddress, macAddress: macFormatted };
        console.log(networkInfo);

        this.store.dispatch({
          type   : SET_PRINTER_CONFIG_VIEW,
          payload: { networkInfo }
        });
      }

      return;
    } catch (err) {
      console.log(`List network info error`);
      console.log(err);
      return err;
    }
  }

  async printZPL(zpl: string, trackingNumber: string, carrier: string = 'UPS'): Promise<any> {
    try {
      if (!zpl || !trackingNumber) {
        console.log(`Missing ZPL and/or tracking number! Can't print shipping label`);
        return `Missing ZPL and/or tracking number! Can't print shipping label`;
      }

      console.log(`zpl: ${JSON.stringify(zpl, null, 2)}`);
      let destPrinter;
      if(carrier === 'FEDEX') {
        console.log('use fedex printer');
        destPrinter = this.getDestinationPrinter('fedex_shipping');
      } else {
        console.log('use ups printer');
        destPrinter = this.getDestinationPrinter('shipping');
      }

      if (!destPrinter) {
        console.log(`Missing destination printer, check printer config`);
        return `Missing destination printer, check printer config`;
      }

      const config = qz.configs.create(destPrinter);
      console.log(config);
      const options = config.getOptions();
      console.log(options);
      const data = [zpl];

      await qz.print(config, data);

      console.log(`Print ZPL success`);
      return `Print ZPL success`;
    } catch (err) {
      console.log(`print ZPL error`);
      console.log(err);

      this.toasterService.pop(
        'error',
        this.translate.instant('LABEL_PRINT_ERROR'),
        this.translate.instant('LABEL_PRINT_ERROR_INFO')
      );

      return err;
    }
  }

  async printAddressLabelZPL(zpl: string, trackingNumber: string): Promise<any> {
    try {
      console.log("printAddressLabelZPL");
      if (!zpl || !trackingNumber) {
        console.log(`Missing ZPL and/or tracking number! Can't print shipping label`);
        return `Missing ZPL and/or tracking number! Can't print shipping label`;
      }

      console.log(`zpl: ${JSON.stringify(zpl, null, 2)}`);
      const destPrinter = this.getDestinationPrinter('addressLabel');

      if (!destPrinter) {
        console.log(`Missing destination printer, check printer config`);
        return `Missing destination printer, check printer config`;
      }

      const config = qz.configs.create(destPrinter);
      console.log(config);
      const options = config.getOptions();
      console.log(options);
      const data = [zpl];

      await qz.print(config, data);

      console.log(`Print printAddressLabelZPL success`);
      return `Print printAddressLabelZPL success`;
    } catch (err) {
      console.log(`print printAddressLabelZPL error`);
      console.log(err);

      this.toasterService.pop(
        'error',
        this.translate.instant('LABEL_PRINT_ERROR'),
        this.translate.instant('LABEL_PRINT_ERROR_INFO')
      );

      return err;
    }
  }

  printIntlInvoice(intlInvoice: any, orderId: string): void {
    if (intlInvoice && orderId) {
      // console.log()
      console.log(`printing intl invoice`,intlInvoice);
      const destPrinter = this.getDestinationPrinter('packingSlip');
      let printInQz = (textInvoice) => {
        const config  = qz.configs.create(destPrinter);
        console.log("textInvoice : ", textInvoice)
        const data = [{
          data  : textInvoice,
          type  : 'pdf',
          format: 'base64'
        }];
        console.log(textInvoice)
        console.log(`sending intl invoice to qz tray`);
        qz.print(config, data)
          .then(() => {
            console.log(`Intl invoice hard copy printed, opening confirmation`);
            
            this.toasterService.pop({
              type   : 'success',
              title  : this.translate.instant('PACKING_SLIP_PRINT_SUCCESS'),
              body   : this.translate.instant('PACKING_SLIP_PRINT_SUCCESS_INFO', [orderId, destPrinter]),
              timeout: 8000
            });

            this.store.dispatch({
              type: SET_MAIN_VIEW,
              payload: { printingInvoice: false }
            });
          })
          .catch(err => {
            console.log(err)
            console.log(`Invoice print error: ${JSON.stringify(err)}`);
            this.store.dispatch({
              type: SET_MAIN_VIEW,
              payload: { printingInvoice: false }
            });
          });
      }

      if (destPrinter) {
        this.store.dispatch({
          type: SET_MAIN_VIEW,
          payload: { printingInvoice: true }
        });
        let textInvoice = '';
        // console.log("INTL INVOICE: ",intlInvoice)
        if(intlInvoice && intlInvoice.includes("intlinvoice_")) {
          const params = {
            Bucket: 'impreso.packingslips',
            Key: intlInvoice
          };

          this.credentialService.s3.getObject(params, function(err, data) {
            if (err) {
              console.error('intlIn err ',err); // an error occurred
            } else {
              // let utf8:any = data.Body;
              // intlInvoice = Buffer.from(utf8).toString('base64');
              // console.log("intlInvoice : ", intlInvoice)
              textInvoice = new TextDecoder('utf-8').decode(data.Body);
              console.log("intlINvoice : ", textInvoice);
              printInQz(textInvoice);
            }
          });
        } else {
          printInQz(intlInvoice);
        }        
      }
    }
  }

  printPackingSlip(jobs: Job[], order: Order, shipment: Shipment, qty: number, isRfid = false, tray : string = null): void {
    if (order && shipment) {
      this.store.dispatch({
        type   : SET_SHIPMENT_DETAILS_VIEW,
        payload: { packingSlipPrinting: true }
      });
      
      this.generatePackingSlip({ barcode: null, jobs, order, shipment }, qty, isRfid)
        .take(qty)
        .subscribe(
          (res: any) => {
            console.log(res);
            const { errorMessage } = res;

            if (errorMessage) {
              console.log(`Error: ${errorMessage}`);
            } else {
              const { pdf_base64 } = res;
              const destPrinter = this.getDestinationPrinter('packingSlip');

              if (destPrinter) {
                let config  = qz.configs.create(destPrinter);
                const options = config.getOptions();

                if(isRfid && tray){
                  config.config.printerTray = tray;
                }
                
                const data = [{
                  data  : pdf_base64,
                  type  : 'pdf',
                  format: 'base64'
                }];
                console.log(`sending to qz tray`);
                qz.print(config, data)
                  .then(res => {
                    console.log(`Print packing slip success`);

                    this.toasterService.pop({
                      type   : 'success',
                      title  : this.translate.instant('PACKING_SLIP_PRINT_SUCCESS'),
                      body   : this.translate.instant('PACKING_SLIP_PRINT_SUCCESS_INFO', [order.id, destPrinter]),
                      timeout: 8000
                    });
                  })
                  .catch(err => console.log(`Printer error: ${JSON.stringify(err)}`));
              }
            }
          },
          err => console.log(`error: ${JSON.stringify(err)}`),
          ()  => {
            this.store.dispatch({
              type   : SET_SHIPMENT_DETAILS_VIEW,
              payload: { packingSlipPrinting: false }
            });
          }
        );

      const addons = [];

      jobs.forEach(job => {
        const addon = specialInstructionPage[job.product.sku];

        if (addon && !addons.find(url => url === addon)) {
          addons.push(addon);
        }
      });

      if (addons.length) {
        this.generatePDFAddons(addons)
          .take(1)
          .subscribe(
            (res: any) => {
              console.log(`generatePDFAddon result`);
              console.log(res);
              const { pdfs } = res;

              if (pdfs) {
                const destPrinter = this.getDestinationPrinter('packingSlip');

                if (destPrinter) {
                  const config  = qz.configs.create(destPrinter);
                  const options = config.getOptions();

                  pdfs.forEach(pdf_base64 => {
                    const data = [{
                      data  : pdf_base64,
                      type  : 'pdf',
                      format: 'base64'
                    }];

                    console.log(`sending to qz tray`);
                    qz.print(config, data)
                      .then(res => {
                        console.log(`Print PDF addon success`);
                      })
                      .catch(err => console.log(`Printer error: ${JSON.stringify(err)}`));
                  });
                }
              }
            },
            err => console.log(`error: ${JSON.stringify(err)}`)
          );
      }
    }
  }
  
  printLaserWorkOrder(batch: Batch, jobs: Job[]): void {
    if (jobs) {
      this.getLaserWorkOrder({ batch, jobs })
        .take(1)
        .subscribe(
          (res: any) => {
            console.log(res);
            const { errorMessage } = res;

            if (errorMessage || !res) {
              console.log(`Error: ${errorMessage || 'Check the Impreso-WorkOrderPrint service'}`);
            } else {
              const { pdf_base64 } = res;

              const destPrinter = this.getDestinationPrinter('workOrder');

              if (destPrinter) {
                const config  = qz.configs.create(destPrinter);
                const options = config.getOptions();
                const data    = [{
                  data  : pdf_base64,
                  type  : 'pdf',
                  format: 'base64'
                }];

                console.log(`sending to qz tray`);
                qz.print(config, data)
                  .then(res => {
                    console.log(`Print work order success`);
                  })
                  .catch(err => {
                    console.log(`Printer error: ${JSON.stringify(err)}`);

                    this.toasterService.pop(
                      'error',
                      this.translate.instant('LABEL_PRINT_ERROR'),
                      this.translate.instant('LABEL_PRINT_ERROR_INFO')
                    );
                  });
              }
            }
          },
          err => console.log(`error: ${JSON.stringify(err)}`),
          ()  => {}
        );
    }
  }

  printWorkOrder(job: Job, recut: boolean, isManual: boolean = false): void {
    if (job) {
      console.log(job)
      this.getWorkOrder({ job, recut })
        .take(1)
        .subscribe(
          (res: any) => {
            console.log(res);
            const { errorMessage } = res;

            if (errorMessage || !res) {
              console.log(`Error: ${errorMessage || 'Check the Impreso-WorkOrderPrint service'}`);
            } else {
              const { pdf_base64 } = res;

              let destPrinter = '';
              if(job.oaDetails.pto && job.oaDetails.pto.pto_preprint_id ){
                destPrinter = this.getDestinationPrinter('ptoWorkOrder');
              }else{  
                destPrinter = this.getDestinationPrinter('workOrder');
              }

              if (destPrinter) {
                // const config  = qz.configs.create(destPrinter);
                const config  = qz.configs.create(destPrinter,
                  { duplex: true } //duplex is true for double sided printing
                );
                const options = config.getOptions();
                const data    = [{
                  data  : pdf_base64,
                  type  : 'pdf',
                  format: 'base64'
                }];

                console.log(`sending to qz tray`);
                qz.print(config, data)
                  .then(res => {
                    console.log(`Print work order success`);

                    const currentUser = this.authService.getCurrentUser();
                    let activityEntries = [];
                    let finalJobUpdate = {};
                    
                    // Only add manual print history entry if it's a manual print
                    if (isManual) {
                      const manualPrintHistoryEntry = {
                        activity: 'WORK_ORDER_PRINTED',
                        note: `WORK_ORDER_PRINTED_MANUALLY`,
                        timestamp: new Date().getTime(),
                        user: currentUser
                      };
                      activityEntries.push(manualPrintHistoryEntry);
                      finalJobUpdate = {
                        ...job,
                        activity_history: [...activityEntries, ...job.activity_history]
                      };
                      this.orderService.updateJob(finalJobUpdate);
                    }

                    const isMtsStatusUpdate = job.makeToStock && !(job.oaDetails.pto && job.oaDetails.pto.pto_preprint_id);

                    if (isMtsStatusUpdate) {
                      const statusChangeHistoryEntry = {
                        activity: CUT,
                        note: '',
                        timestamp: new Date().getTime(),
                        user: currentUser
                      };
                      activityEntries.push(statusChangeHistoryEntry);
                      finalJobUpdate = {
                        ...job,
                        activity_history: [...activityEntries, ...job.activity_history],
                        status: CUT,
                        previous_status: QUEUED
                      };
                      this.orderService.updateJob(finalJobUpdate);
                    }
                  })
                  .catch(err => {
                    console.log(`Printer error: ${JSON.stringify(err)}`);

                    this.store.dispatch({
                      type: SET_BATCH_LIST_VIEW,
                      payload: { mtsWorkOrdersPrinting: false }
                    });

                    this.toasterService.pop(
                      'error',
                      this.translate.instant('LABEL_PRINT_ERROR'),
                      this.translate.instant('LABEL_PRINT_ERROR_INFO')
                    );
                  });
              }else{
                console.log('Missing printer config setup')
                this.toasterService.pop(
                  'error',
                  this.translate.instant('LABEL_PRINT_ERROR'),
                  this.translate.instant('LABEL_PRINT_ERROR_INFO')
                );
              }
            }
          },
          err => console.log(`error: ${JSON.stringify(err)}`),
          ()  => {}
        );
    }
  }

  printRfidWorkOrder(job: Job): void {
    if (job) {
      console.log(job)
      this.getRfidWorkOrder({ job })
        .take(1)
        .subscribe(
          (res: any) => {
            console.log(res);
            const { errorMessage } = res;

            if (errorMessage || !res) {
              console.log(`Error: ${errorMessage || 'Check the Impreso-RfidWorkOrder service'}`);
            } else {
              const { pdf_base64 } = res;

              let destPrinter = '';

              //TO-DO: SETUP PROOF DESITNATION PRINTER 
              //FOR NOW JUST USE WORKORDER IS TEMP
              destPrinter = this.getDestinationPrinter('rfidWorkOrder');

              if (destPrinter) {
                const config  = qz.configs.create(destPrinter);
                const options = config.getOptions();
                const data    = [{
                  data  : pdf_base64,
                  type  : 'pdf',
                  format: 'base64'
                }];

                console.log(`sending to qz tray`);
                qz.print(config, data)
                  .then(res => {
                    console.log(`Print proof success`);

                    // // Add history entry for manual print
                    // const currentUser = this.authService.getCurrentUser(); // Assuming this returns username string or null
                    // const historyEntry = {
                    //   activity: 'WORK_ORDER_PRINTED',
                    //   note: `WORK_ORDER_PRINTED_MANUALLY`,
                    //   timestamp: new Date().getTime(),
                    //   user: currentUser // Pass username string or null
                    // };
                    // const jobWithHistory = {
                    //   ...job,
                    //   activity_history: [historyEntry, ...job.activity_history]
                    // };
                    // // Update job immediately with history
                    // this.orderService.updateJob(jobWithHistory);
                  })
                  .catch(err => {
                    console.log(`Printer error: ${JSON.stringify(err)}`);

                    this.toasterService.pop(
                      'error',
                      this.translate.instant('LABEL_PRINT_ERROR'),
                      this.translate.instant('LABEL_PRINT_ERROR_INFO')
                    );
                  });
              }else{
                console.log('Missing printer config setup')
                this.toasterService.pop(
                  'error',
                  this.translate.instant('LABEL_PRINT_ERROR'),
                  this.translate.instant('LABEL_PRINT_ERROR_INFO')
                );
              }
            }
          },
          err => console.log(`error: ${JSON.stringify(err)}`),
          ()  => {}
        );
    }
  }

  printPtoArticleChecklist(job: Job): void {
    if (!job) return;

    const payload: any = { job };
    this.getPtoArticleChecklist(payload)
      .take(1)
      .subscribe(
        (res: any) => {
          const errorMessage = res ? res.errorMessage : undefined;
          if (errorMessage || !res) {
            console.log(`Error: ${errorMessage || 'Check the Impreso-PTOArticleChecklist service'}`);
            return;
          }

          const { pdf_base64 } = res;
          let destPrinter = this.getDestinationPrinter('ptoArticleChecklist');
          if (!destPrinter) {
            console.log('Missing printer config setup');
            this.toasterService.pop('error', this.translate.instant('LABEL_PRINT_ERROR'), this.translate.instant('LABEL_PRINT_ERROR_INFO'));
            return;
          }

          const config  = qz.configs.create(destPrinter);
          const data    = [{ data: pdf_base64, type: 'pdf', format: 'base64' }];
          qz.print(config, data)
            .then(() => console.log('Print PTO Article Checklist success'))
            .catch(err => {
              console.log(`Printer error: ${JSON.stringify(err)}`);
              this.toasterService.pop('error', this.translate.instant('LABEL_PRINT_ERROR'), this.translate.instant('LABEL_PRINT_ERROR_INFO'));
            });
        },
        err => console.log(`error: ${JSON.stringify(err)}`),
        ()  => {}
      );
  }

  printWorkOrders(data) {
    const destPrinter = this.getDestinationPrinter('workOrder');
    if (destPrinter) {
      const config  = qz.configs.create(destPrinter);
      const options = config.getOptions();
      let chain = [];
      for(let i = 0; i < data.length; i++) {
        ((i_) => {
            //setup this chain link
            let link = function() {
              return qz.print(config, data[i_]);
            };

            chain.push(link);
        })(i);
          //closure ensures this promise's concept of `i` doesn't change
      }
      //can be .connect or `Promise.resolve()`, etc
      let firstLink = new RSVP.Promise(function(r, e) { r(); }); 
      let lastLink = null;
      chain.reduce((sequence, link) => {
          lastLink = sequence.then(link);
          return lastLink;
      }, firstLink);

      //this will be the very last link in the chain
      lastLink.catch(function(err) {
          console.error(err);
      });
    }
  }

  async createWorkOrderData(job: Job, recut: boolean){
    try {
      if (job) {
        let response = await this.getWorkOrderData({ job, recut });
        console.log('response ',response)
        return response;
      }
    } catch (err) {
      console.log(`getPrinterList error`);
      console.log(err);
      return [];
    }
  }

  printPackagingLabel(labelContent: any): void {
    console.log("🚀 ~ labelContent:", labelContent)
    if (labelContent) {
      this.generatePackagingLabel(labelContent)
        .take(1)
        .subscribe(
          (res: any) => {
            console.log(res);
            const { errorMessage } = res;

            if (errorMessage || res === null) {
              console.log(`Error: ${errorMessage}`);
            } else {
              const { pdf_base64 } = res;

              // const destPrinter = this.getDestinationPrinter('packagingLabel');
              let destPrinter = '';
              
              if(labelContent.ptoDesignName && labelContent.ptoPreprintId ){
                destPrinter = this.getDestinationPrinter('ptoPackagingLabel');
              }else{  
                destPrinter = this.getDestinationPrinter('packagingLabel');
              }

              if (destPrinter) {
                const config = qz.configs.create(destPrinter, {
                  size        : {width: 4, height: 6.15},
                  margins     : { top: 0, right: 0, bottom: -0.145, left: 0 },
                  units       : "in",
                  scaleContent: false,
                  colorType   : "color",
                  encoding    : "UTF-8",
                  copies      : labelContent.copies
               });
                
                const data = [{
                  data  : pdf_base64,
                  type  : 'pdf',
                  format: 'base64'
                }];

                console.log(`sending to qz tray`);
                qz.print(config, data)
                  .then(res => {
                    console.log(`Print packaging label success`);

                    this.toasterService.pop({
                      type   : 'success',
                      title  : this.translate.instant('PACKAGING_LABEL_PRINT_SUCCESS'),
                      body   : this.translate.instant('PACKAGING_LABEL_PRINT_SUCCESS_INFO', [labelContent.jobId, destPrinter]),
                      timeout: 5000
                    });
                  }).catch(err => console.log(`Printer error: ${JSON.stringify(err)}`));
              }else{
                console.log('Missing printer config setup')
                this.toasterService.pop(
                  'error',
                  this.translate.instant('LABEL_PRINT_ERROR'),
                  this.translate.instant('LABEL_PRINT_ERROR_INFO')
                );
              }
            }
          },
          err => console.log(`error: ${JSON.stringify(err)}`),
          ()  => {
            this.store.dispatch({
              type   : SET_JOB_DETAILS_VIEW,
              payload: { packagingLabelPrinting: false }
            });
          }
        );
    }
  }

  sendToGHSPrinter(key: string, copies: string, modalref: BsModalRef): void {
    this.getGHSImposedPDF(key).take(1).subscribe(res => {
      const imposedPDF = res;
      const destPrinter = this.getDestinationPrinter('ghs');

      if (destPrinter) {
        const config = qz.configs.create(destPrinter, { copies });
  
        const payload = [{
          type: 'pdf',
          data: imposedPDF,
        }];
  
        qz.print(config, payload)
        .then(res => {
          console.log('Imprinted GHS PDF Sent to GHS Printer');
          modalref.hide();
        }, err => {
          console.log('Error with GHS printing');
        });

      } else {
        console.log('GHS Printer not set in printer configuration');
      }
    }, err => console.log(err));
  }

  setPrinterDestination(printerDestination: string, selectedPrinter: string): void {
    if (printerDestination) {
      const printConfig = this.getPrintConfig();

      switch (printerDestination) {
        case 'shipping': 
          printConfig.shipping = selectedPrinter;
          break;
        case 'fedex_shipping': 
          printConfig.fedex_shipping = selectedPrinter;
          break;
        case 'packingSlip': 
          printConfig.packingSlip = selectedPrinter;
          break;
        case 'packagingLabel': 
          printConfig.packagingLabel = selectedPrinter;
          break;
        case 'workOrder': 
          printConfig.workOrder = selectedPrinter;
          break;
        case 'rfidWorkOrder':
          printConfig.rfidWorkOrder = selectedPrinter;
          break;
        case 'ptoArticleChecklist':
          printConfig.ptoArticleChecklist = selectedPrinter;
          break;
        case 'ghs':
          printConfig.ghs = selectedPrinter;
          break;
        case 'addressLabel':
          printConfig.addressLabel = selectedPrinter;
          break;
        case 'ptoWorkOrder':
          printConfig.ptoWorkOrder = selectedPrinter;
          break;
        case 'ptoPackagingLabel':
          printConfig.ptoPackagingLabel = selectedPrinter;
          break;
        case 'rfidInnerCaseLabel':
          printConfig.rfidInnerCaseLabel = selectedPrinter;
          break;
        case 'rfidBoxLabels':
          printConfig.rfidBoxLabels = selectedPrinter;
          break;
        default: 
          printConfig.defaultPrinter = selectedPrinter;
      }

      console.log(`new print config: ${JSON.stringify(printConfig)}`);

      localStorage.setItem('printConfig', JSON.stringify(printConfig));
    }
  }

  setQZCredentials(): void {
    qz.security.setCertificatePromise((resolve, reject) => {
      // console.log('SET CERTIFICATE PROMISE');
      this.getSignedCertificate()
        .take(1)
        .subscribe(
          (res: any) => {
            const cert = this.ABToString(res['data']);
            console.log(`qz tray certficate: ${cert}`);
            resolve(cert);
          },
          (error: Error) => {
            console.log(`QZ Cert retrieval error: ${JSON.stringify(error)}`);
            reject(error);
          }
        );
    });

    qz.security.setSignaturePromise(toSign => {
      // console.log('SET SIGNATURE PROMISE');
      return (resolve, reject) => {
        this.sign(toSign)
          .take(1)
          .subscribe(
            (res: any) => {
              console.log(`qz tray signature promise result: ${JSON.stringify(res, null, 2)}`);
              resolve(res);
            },
            (error: Error) => {
              console.log(`QZ signature process error: ${JSON.stringify(error)}`);
              reject(error);
            }
          );
      }
    });


  }

  startConnection(config: any) {
    if (qz.websocket.isActive()) {
      this.store.dispatch({
        type   : SET_PRINTER_CONFIG_VIEW,
        payload: { status: ACTIVE }
      });
    } else {
      this.store.dispatch({
        type   : SET_PRINTER_CONFIG_VIEW,
        payload: { status: WAITING }
      });

      qz.websocket
        .connect(config)
        .then(res => {
          this.store.dispatch({
            type   : SET_PRINTER_CONFIG_VIEW,
            payload: { status: ACTIVE }
          });

          this.authService
            .logoutTrigger
            .subscribe(() => this.endConnection());

          this.findVersion();

          qz.websocket
            .setClosedCallbacks(event => {
              this.endConnection();
            });
        })
        .catch(err => {
          this.store.dispatch({
            type   : SET_PRINTER_CONFIG_VIEW,
            payload: { status: INACTIVE }
          });

          console.log(`Start QZ tray connection error`);
        });
    }
  }
  
  createZpl() {
    const zplify = (function() {

      function getArraySum(arrayOfNums) {
        return arrayOfNums.reduce((sum, proportion) => {
          if (!isNaN(proportion) && proportion > 0 && proportion <= 1) {
            return sum+proportion;
          }
          return 0;
        }, 0);
      }
    
      function getColumnWidths(columnRatios, columns, tableWidth) {
    
        const columnRatiosSum = getArraySum(columnRatios);
    
        if (columnRatios.length === columns.length
            && (Math.abs(columnRatiosSum - 1) < Number.EPSILON)) {
          return columnRatios.map(proportion => Math.floor(proportion*tableWidth));
        }
        return columns.map(col => Math.floor(tableWidth/columns.length));
      };
    
      function createBorderBox (labelPadding, tableHeight, tableWidth, borderWidth) {
        return '^FO'+labelPadding
               +','+labelPadding
               +'^GB'+tableWidth
               +','+tableHeight
               +','+borderWidth
               +'^FS\n';
      };
    
      function createVerticalSeparators(
        columns,
        columnWidths,
        labelPadding,
        tableHeight,
        borderWidth
      ) {
        let runningSum = 0;
        let separators = '';
        columns.forEach((column, index) => {
          if (index === columns.length-1) return;
          runningSum += columnWidths[index];
          separators += '^FO'+(labelPadding+runningSum)
                   +','+labelPadding
                   +'^GB1,'+tableHeight
                   +','+borderWidth
                   +'^FS\n';
        });
        return separators;
      };
    
      function createHorizontalSeparators(
        rows,
        rowHeight,
        labelPadding,
        tableWidth,
        borderWidth
      ) {
        let runningSum = 0;
        let separators = '';
        rows.forEach((row, index) => {
          if (index === rows.length-1) return;
          runningSum += rowHeight;
          separators += '^FO'+labelPadding
                   +','+(labelPadding+runningSum)
                   +'^GB'+tableWidth
                   +',1'
                   +','+borderWidth
                   +'^FS\n';
        });
        return separators;
      };
    
      function createAllCellContent(
        fontSize,
        labelPadding,
        rowHeight,
        columns,
        rows,
        cellPadding,
        columnWidths
      ) {
        const cells = [];
        cells.push('^CF0,'+fontSize+','+fontSize);
        let runningYSum = labelPadding;
        rows.forEach((row, rowIndex) => {
          const yOffset = runningYSum+(rowHeight-fontSize)/1.5;
          runningYSum += rowHeight;
    
          let runningXSum = labelPadding;
          columns.forEach((col, colIndex) => {
            let xOffset = runningXSum;
            if (rowIndex !== 0) {
              xOffset += cellPadding;
            }
            runningXSum += columnWidths[colIndex];
            let cell = '^FO'+xOffset+','+yOffset;
            if (rowIndex === 0) {
              cell += '^A0N,'
                   +fontSize
                   +','+(fontSize+2)
                   +'^FB'+columnWidths[colIndex]
                   +',1,0,C^FD';
            }
            else {
              cell += '^FD';
            }
            cell += (typeof row[colIndex] === 'undefined' ? '' : row[colIndex])+'^FS';
            cells.push(cell);
          });
        });
    
        return cells.join('\n') + '\n';
      };
    
      function generateTable({ rows = [], config = {} }) {
        const columns = rows[0]; // 
    
        const {
          labelWidth: labelWidthInches = 4, // inches
          labelHeight: labelHeightInches = 6, // inches
          printDensity = 203, // dots per inch (dpi)
          labelPadding = 50, // dots
          borderWidth = 3, // dots
          columnRatios = [], // array of decimal ratios that must add up to 1
          fontSize = 30, // dots
          maxRowHeight = 2*fontSize, // dots
          rowHeight = maxRowHeight, // dots
          cellPadding = 20 // dots
        } : any = config;
    
        const labelWidth = labelWidthInches*printDensity;
        const labelHeight = labelHeightInches*printDensity;
    
        const tableWidth = labelWidth - 2*labelPadding;
        const tableHeight = rows.length*rowHeight;
    
        const columnWidths = getColumnWidths(columnRatios, columns, tableWidth);
    
        // there are 6 steps
    
        // 1. Create opening
        let zplTable = '^XA\n';
    
        // 2. Create border box
        zplTable += createBorderBox(labelPadding, tableHeight, tableWidth, borderWidth);
    
        // 3. Create vertical separators
        zplTable += createVerticalSeparators(columns, columnWidths, labelPadding, tableHeight, borderWidth);
    
        // 4. Create horizontal separators
        zplTable += createHorizontalSeparators(rows, rowHeight, labelPadding, tableWidth, borderWidth);
    
        // 5. Fill in content
        zplTable += createAllCellContent(fontSize, labelPadding, rowHeight, columns, rows, cellPadding, columnWidths);
    
        // 6. Create closing
        zplTable += '^XZ';
    
        return zplTable;
      };
      function chunkArray(myArray, chunk_size){
        var results = [];
        
        while (myArray.length) {
            results.push(myArray.splice(0, chunk_size));
        }
        
        return results;
      };
      return {
        getArraySum,
        getColumnWidths,
        createBorderBox,
        createVerticalSeparators,
        createHorizontalSeparators,
        createAllCellContent,
        generateTable,
        chunkArray
      };
    });
    return zplify();
  }

  generatePackagingPayload(job: Job, order: Order): any[] {
    // CONFIGURABLE VALUES
    const keycardsPerInnerBox = 200;
    const innerBoxesPerOuterBox = 10;
    const keycardsPerOuterBox = keycardsPerInnerBox * innerBoxesPerOuterBox;

    // Calculate boxes needed
    const totalFullOuterBoxes = Math.floor(job.qty / keycardsPerOuterBox);
    const remainingKeycards = job.qty % keycardsPerOuterBox;

    const payload = [];

    // Add full boxes (2000 keycards each)
    if (totalFullOuterBoxes > 0) {
      payload.push({
        poNumber: order.poNumber,
        salesOrderNumber: order.salesOrderNumber,
        rfid_description: job.rfid_description,
        rfid_chipType: job.rfid_chipType,
        rfid_code: job.rfid_code,
        qty: keycardsPerOuterBox, // 2000 keycards per full box
        rfid_coo: job.rfid_coo,
        outsource: order.customer.outsource,
        isFullBox: true
      });
    }

    // Add partial box if there are remaining keycards
    if (remainingKeycards > 0) {
      payload.push({
        poNumber        : order.poNumber,
        salesOrderNumber: order.salesOrderNumber,
        rfid_description: job.rfid_description,
        rfid_chipType   : job.rfid_chipType,
        rfid_code       : job.rfid_code,
        qty             : remainingKeycards,
        rfid_coo        : job.rfid_coo,
        outsource       : order.customer.outsource,
        isFullBox       : false
      });
    }

    return payload;
  }

  async printBoxLabels(labels: string[]): Promise<void> {
    try {
      for (let i = 0; i < labels.length; i++) {
        if (labels[i]) {
          await this.printRFID(
            labels[i],
            'rfidBoxLabels',
            `Outer Box Label ${i + 1}`,
            1
          );
        }
      }
    } catch (error) {
      console.error('Error printing box labels:', error);
      throw error;
    }
  }

  async printRFID(dataInput: string | any, printerDestination: string, jobName: string, qty: number = 1, tray : string = null): Promise<any> {
    try {
      const destPrinter = this.getDestinationPrinter(printerDestination);

      if (!destPrinter) {
        console.log(`Missing destination printer, check printer config`);
        return `Missing destination printer, check printer config`;
      }

      let config = qz.configs.create(destPrinter);
      const options = config.getOptions();
      options.jobName = jobName;

      // Determine data format based on printer destination or data type
      let data;
      switch (printerDestination) {
        case 'rfidBoxLabels':
          // ZPL format
          data = [dataInput];  // Simple array with single input
          break;
        case 'rfidInnerCaseLabel':
          if (!dataInput) {
            console.log(`Missing PDF data! Can't print PDF label`);
            return `Missing PDF data! Can't print PDF label`;
          }

          if(tray){
            config.config.printerTray = tray;
          }

          data = [{
            data: dataInput,
            type: 'pdf'
          }];
          break;
        default:
          if (typeof dataInput === 'string' && dataInput.trim().startsWith('^XA')) {
            // Looks like ZPL
            data = [dataInput];
          } else {
            if (!dataInput) {
              console.log(`Missing print data! Can't print label`);
              return `Missing print data! Can't print label`;
            }
            data = [{
              data: dataInput,
              type: 'pdf'
            }];
          }
      }

      // Handle multiple copies by repeating the print command
      for (let i = 0; i < qty; i++) {
        await qz.print(config, data);
      }

      console.log(`Print success - ${qty} copies`);
      return `Print success - ${qty} copies`;

    } catch (err) {
      console.log(`Print error`);
      console.log(err);

      this.toasterService.pop(
        'error', 
        this.translate.instant('PRINT_ERROR'),
        this.translate.instant('PRINT_ERROR_INFO')
      );

      return err;
    }
  }
}


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/services/printer.service.ts